import * as React from 'react';
// components
import Text from 'components/Text';
import Divider from 'components/Divider';
import Checkbox from 'components/Checkbox';
import MediaObject from 'components/MediaObject';
import { GetLanguageSet_translations_getLanguageSet_data_languageCounts as LanguageCounts } from 'translations/queries/__generated__/GetLanguageSet';
import './styles/TranslationsHoverCard.scss';
import { mergeLanguageResults } from 'translations/utils';
import TooltipTrigger from 'components/TooltipTrigger';
import Icon from 'components/Icon';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import { TranslationsHoverCardRow } from 'translations/components/TranslationsHoverCardRow';
import { Menu } from 'components/Menu';
import MenuItem from 'components/MenuItem';
import { EditedMessageTranslationsType } from 'translations/types';

type Props = {
  languageCounts: LanguageCounts[];
  detectedMessageLanguage: string;
  toggleTranslate: (shouldTranslate: boolean) => void;
  shouldTranslate: boolean;
  onMenuItemSelect: (languageCode: string) => void;
  backToOriginalCallback?: () => void;
  translationsPreviewActive: boolean;
  toggleHoverCardVisibility: () => void;
  editedTranslations: EditedMessageTranslationsType;
};

type State = {
  mergedLanguageResults: LanguageCounts[];
};

// Generic presentation component that should (hopefully) be reusable across
// both ChatBroadcast and Chat composers.
export class TranslationsHoverCard extends React.Component<Props, State> {
  state: State = {
    mergedLanguageResults: [],
  };

  componentDidMount = () => {
    this.updateLanguageCount();
  };

  componentWillUpdate(prevProps: Readonly<Props>) {
    if (
      prevProps.languageCounts !== this.props.languageCounts ||
      prevProps.detectedMessageLanguage !== this.props.detectedMessageLanguage
    ) {
      this.updateLanguageCount();
    }
  }

  updateLanguageCount = () => {
    const { languageCounts, detectedMessageLanguage } = this.props;

    let noPreferenceCount: LanguageCounts = { languageCode: 'none', count: 0 } as LanguageCounts;
    let originalLanguageCount = {
      __typename: 'LanguageCount',
      languageCode: detectedMessageLanguage,
      count: 0,
    } as LanguageCounts;
    const otherLanguageCounts: LanguageCounts[] = [];
    mergeLanguageResults(languageCounts).forEach(lc => {
      if (lc.languageCode === 'none') {
        noPreferenceCount = lc;
      } else if (lc.languageCode === detectedMessageLanguage) {
        originalLanguageCount = lc;
      } else {
        otherLanguageCounts.push(lc);
      }
    });

    // Rows will be displayed in array order, so we need to sort as follows:
    //   first row is original language, if it's been determined
    //   last row is the 'No preference' row -- for any recipients who do not have a preferred language set
    //   rows in between are in descending order of count
    // Note that if the original language determination comes in *after* the rows have been displayed,
    //   the row order doesn't change.  OK'd with Product Design
    otherLanguageCounts.sort((a, b) => b.count - a.count);
    this.setState({
      mergedLanguageResults: [originalLanguageCount, ...otherLanguageCounts, noPreferenceCount],
    });
  };

  renderLanguageOptions = () => {
    const {
      shouldTranslate,
      detectedMessageLanguage,
      onMenuItemSelect,
      editedTranslations,
    } = this.props;
    const { mergedLanguageResults } = this.state;

    return mergedLanguageResults.map((languageCount: LanguageCounts) => {
      const { languageCode, count } = languageCount;

      const languageMatches = languageCode === detectedMessageLanguage && languageCode !== 'none';

      return (
        count !== 0 && (
          <TranslationsHoverCardRow
            {...this.props}
            key={languageCode}
            disabled={languageMatches || !shouldTranslate}
            onSelect={() => {
              onMenuItemSelect(languageCode);
              this.props.toggleHoverCardVisibility();
            }}
            languageCount={languageCount}
            editedTranslations={editedTranslations}
          />
        )
      );
    });
  };

  renderWithTranslationsPreview = () => {
    const { backToOriginalCallback } = this.props;
    return (
      <Menu rowHover className="TranslationsHoverCard-body" hideOnSelect={false} {...this.props}>
        <Text align="left">{this.renderLanguageOptions()}</Text>
        <Divider size="xsmall" />
        <MenuItem
          menuKey="back_to_original"
          selected
          onSelect={() => {
            if (backToOriginalCallback) {
              backToOriginalCallback();
            }
          }}>
          <Text size="small" color="mascot" lineHeight="flush">
            Back to original
          </Text>
        </MenuItem>
      </Menu>
    );
  };

  renderWithOutTranslationsPreview = () => {
    const { toggleTranslate, shouldTranslate } = this.props;

    return (
      <Menu rowHover className="TranslationsHoverCard-body" hideOnSelect {...this.props}>
        <MediaObject figure="right" className="TranslationsHoverCard-first-menu-item">
          <Checkbox
            className="TranslationsHoverCard-checkbox"
            flush
            onChange={() => {
              toggleTranslate(!shouldTranslate);
            }}
            label={<Text weight="semibold">Translate this message</Text>}
            checked={shouldTranslate}
          />
          <TooltipTrigger tooltip="Learn More" placement="top">
            <Link color="graphite" href={LinkConstants.TRANSLATIONS_TOOLTIP_HELP} target="_blank">
              <Icon name="question-circle" />
            </Link>
          </TooltipTrigger>
        </MediaObject>
        <Divider size="xsmall" />
        <Text align="left">{this.renderLanguageOptions()}</Text>
      </Menu>
    );
  };

  render() {
    const { translationsPreviewActive } = this.props;
    return translationsPreviewActive
      ? this.renderWithTranslationsPreview()
      : this.renderWithOutTranslationsPreview();
  }
}
